import { useEffect, useMemo, useState } from 'react'
import { Users, Search, UserPlus, Building2, Loader2, X, User } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Button } from 'shared/ui/button'
import { Badge } from 'shared/ui/badge'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'shared/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import {
  type ContractorCompany,
  type ContractorWorker,
  getContractorCompanies,
  getContractorWorkers,
} from '../api/contractorService'
import { isSameWorkerDirectoryRecord, resolveWorkerCompanyName, resolveWorkerJobRole } from '../utils/workerSelection'

export interface WorkerSelectionItem {
  workerId: string
  companyId: string
  companyName: string
  orgName?: string | null
  fullName: string
  username?: string | null
  imageType?: string | null
  imagePath?: string | null
  jobRole: string
  status: string
  cardNumber?: string | null
  roleInPermit?: string | null
}

interface WorkersSelectionProps {
  tenantId: string
  selectedWorkers: WorkerSelectionItem[]
  setSelectedWorkers: (workers: WorkerSelectionItem[]) => void
}

const DEFAULT_PAGE_SIZE = 15
const COMPANY_PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 300

const formatCompanyName = (value?: string | null) => {
  if (!value) return '-'
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : '-'
}

const normalizeOptionalText = (value?: string | null) => {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const resolveIdentifierDisplay = (worker: {
  cardNumber?: string | null
}) => {
  const cardNumber = worker.cardNumber?.trim()
  if (cardNumber) {
    return `Card ID: ${cardNumber}`
  }

  return null
}

const mergeWorkerDisplayRecord = (
  records: ContractorWorker[],
  selectedMatch?: WorkerSelectionItem | null,
): ContractorWorker => {
  const primary = records[0]
  const preferred =
    records.find(
      (item) =>
        normalizeOptionalText(item.cardNumber) ||
        normalizeOptionalText(item.imagePath) ||
        normalizeOptionalText(item.orgName) ||
        normalizeOptionalText(item.username),
    ) ?? primary

  const resolvedCompanyName =
    normalizeOptionalText(selectedMatch?.companyName) ??
    records
      .map((item) => resolveWorkerCompanyName(item))
      .find((value) => normalizeOptionalText(value)) ??
    primary.companyName

  const resolvedOrgName =
    normalizeOptionalText(selectedMatch?.orgName) ??
    records
      .map((item) => normalizeOptionalText(item.orgName))
      .find((value) => value) ??
    preferred.orgName

  const resolvedCardNumber =
    normalizeOptionalText(selectedMatch?.cardNumber) ??
    records
      .map((item) => normalizeOptionalText(item.cardNumber))
      .find((value) => value) ??
    preferred.cardNumber

  const resolvedJobRole =
    normalizeOptionalText(selectedMatch?.jobRole) ??
    records
      .map((item) => resolveWorkerJobRole(item))
      .find((value) => normalizeOptionalText(value)) ??
    primary.jobRole

  return {
    ...primary,
    companyName: resolvedCompanyName ?? primary.companyName,
    company: normalizeOptionalText(primary.company) ?? resolvedCompanyName ?? primary.company,
    orgName: resolvedOrgName ?? primary.orgName,
    cardNumber: resolvedCardNumber ?? primary.cardNumber,
    jobRole: resolvedJobRole ?? primary.jobRole,
    position: normalizeOptionalText(preferred.position) ?? resolvedJobRole ?? preferred.position,
    username: normalizeOptionalText(selectedMatch?.username) ?? preferred.username ?? primary.username,
    imageType: normalizeOptionalText(selectedMatch?.imageType) ?? preferred.imageType ?? primary.imageType,
    imagePath: normalizeOptionalText(selectedMatch?.imagePath) ?? preferred.imagePath ?? primary.imagePath,
  }
}

const buildWorkerImageSrc = (worker: ContractorWorker): string | null => {
  const rawPath = worker.imagePath?.trim()
  if (!rawPath) {
    return null
  }

  if (rawPath.startsWith('data:') || rawPath.startsWith('http://') || rawPath.startsWith('https://')) {
    return rawPath
  }

  const typeRaw = worker.imageType?.trim()
  if (!typeRaw) {
    return rawPath
  }

  const normalizedType = typeRaw.toLowerCase() === 'jpg' ? 'jpeg' : typeRaw
  const mime = normalizedType.includes('/') ? normalizedType.replace('/jpg', '/jpeg') : `image/${normalizedType}`
  return `data:${mime};base64,${rawPath}`
}

const buildWorkerSelectionImageSrc = (worker: WorkerSelectionItem): string | null => {
  const rawPath = worker.imagePath?.trim()
  if (!rawPath) {
    return null
  }

  if (rawPath.startsWith('data:') || rawPath.startsWith('http://') || rawPath.startsWith('https://')) {
    return rawPath
  }

  const typeRaw = worker.imageType?.trim()
  if (!typeRaw) {
    return rawPath
  }

  const normalizedType = typeRaw.toLowerCase() === 'jpg' ? 'jpeg' : typeRaw
  const mime = normalizedType.includes('/') ? normalizedType.replace('/jpg', '/jpeg') : `image/${normalizedType}`
  return `data:${mime};base64,${rawPath}`
}

type WorkerAvatarProps = {
  imageSrc: string | null
  alt: string
  sizePx?: number
  iconSizePx?: number
}

const WorkerAvatar = ({ imageSrc, alt, sizePx = 60, iconSizePx = 32 }: WorkerAvatarProps) => {
  const [hasError, setHasError] = useState(false)
  const showImage = Boolean(imageSrc) && !hasError
  return (
    <div
      aria-label={alt}
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-gray-500"
      style={{
        width: sizePx,
        height: sizePx,
      }}
    >
      {showImage ? (
        <img
          src={imageSrc ?? undefined}
          alt={alt}
          width={sizePx}
          height={sizePx}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover object-top"
          onError={() => setHasError(true)}
        />
      ) : (
        <User style={{ width: iconSizePx, height: iconSizePx }} />
      )}
    </div>
  )
}

/**
 * Worker selection dialog powered by contractor directory API. Supports filtering by company,
 * searching by free text, pagination and displays the current selection in a prominent block.
 */
export function WorkersSelection({ tenantId, selectedWorkers, setSelectedWorkers }: WorkersSelectionProps) {
  void tenantId
  // tenantId is intentionally ignored for permit-request worker lookup.
  const [isOpen, setIsOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | 'all'>('all')
  const [companySearchInput, setCompanySearchInput] = useState('')
  const [companySearchTerm, setCompanySearchTerm] = useState('')
  const [companyPage, setCompanyPage] = useState(1)
  const [companyTotalPages, setCompanyTotalPages] = useState(0)
  const [companyTotalItems, setCompanyTotalItems] = useState(0)

  const [companies, setCompanies] = useState<ContractorCompany[]>([])
  const [isCompanyLoading, setIsCompanyLoading] = useState(false)

  const [workers, setWorkers] = useState<ContractorWorker[]>([])
  const [workersPage, setWorkersPage] = useState(1)
  const [workersTotalPages, setWorkersTotalPages] = useState(0)
  const [workersTotalItems, setWorkersTotalItems] = useState(0)
  const [isWorkersLoading, setIsWorkersLoading] = useState(false)

  const hasSelection = selectedWorkers.length > 0

  useEffect(() => {
    const pendingWorkers = selectedWorkers.filter((worker) => !worker.orgName?.trim())
    if (pendingWorkers.length === 0) {
      return
    }

    let cancelled = false
    const searchCache = new Map<string, Promise<Awaited<ReturnType<typeof getContractorWorkers>>>>()

    const resolveSearchTerm = (worker: WorkerSelectionItem) => {
      const cardNumber = worker.cardNumber?.trim()
      if (cardNumber) {
        return cardNumber
      }

      const fullName = worker.fullName?.trim()
      if (fullName) {
        return fullName
      }

      const username = worker.username?.trim()
      return username || null
    }

    const enrichWorkers = async () => {
      const updatedWorkers = await Promise.all(
        selectedWorkers.map(async (worker) => {
          if (worker.orgName?.trim()) {
            return worker
          }

          const searchTerm = resolveSearchTerm(worker)
          if (!searchTerm) {
            return worker
          }

          if (!searchCache.has(searchTerm)) {
            searchCache.set(
              searchTerm,
              getContractorWorkers({
                // tenantId,
                // Worker lookup in permit-request should not be restricted by tenant.
                search: searchTerm,
                pageNumber: 1,
                pageSize: 50,
              }),
            )
          }

          try {
            const response = await searchCache.get(searchTerm)!
            const matches = response.items?.filter((item) => isSameWorkerDirectoryRecord(worker, item)) ?? []
            if (matches.length === 0) {
              return worker
            }

            const preferredMatch =
              matches.find(
                (item) =>
                  item.orgName?.trim() ||
                  item.cardNumber?.trim() ||
                  item.imagePath?.trim() ||
                  item.username?.trim(),
              ) ?? matches[0]

            const resolvedCompanyName =
              matches
                .map((item) => resolveWorkerCompanyName(item))
                .find((value) => typeof value === 'string' && value.trim().length > 0) ?? worker.companyName

            const resolvedOrgName =
              matches
                .map((item) => item.orgName?.trim())
                .find((value) => typeof value === 'string' && value.length > 0) ?? worker.orgName

            const resolvedCardNumber =
              matches
                .map((item) => item.cardNumber?.trim())
                .find((value) => typeof value === 'string' && value.length > 0) ?? worker.cardNumber

            return {
              ...worker,
              companyName: resolvedCompanyName,
              orgName: resolvedOrgName,
              cardNumber: resolvedCardNumber,
              username: preferredMatch.username ?? worker.username,
              imageType: preferredMatch.imageType ?? worker.imageType,
              imagePath: preferredMatch.imagePath ?? worker.imagePath,
              jobRole: worker.jobRole || resolveWorkerJobRole(preferredMatch),
            }
          } catch {
            return worker
          }
        }),
      )

      if (cancelled) {
        return
      }

      const hasChanges = updatedWorkers.some((worker, index) => worker.orgName !== selectedWorkers[index]?.orgName)
      if (hasChanges) {
        setSelectedWorkers(updatedWorkers)
      }
    }

    enrichWorkers()

    return () => {
      cancelled = true
    }
  }, [selectedWorkers, setSelectedWorkers, tenantId])

  useEffect(() => {
    if (!isOpen) return

    let cancelled = false
    setIsCompanyLoading(true)

    getContractorCompanies({
      // tenantId,
      // Worker company lookup in permit-request should not be restricted by tenant.
      pageNumber: companyPage,
      pageSize: COMPANY_PAGE_SIZE,
      search: companySearchTerm || undefined,
    })
      .then((result) => {
        if (cancelled) return
        setCompanyTotalPages(result.totalPages ?? 0)
        setCompanyTotalItems(result.totalItems ?? 0)
        const incoming = result.items ?? []
        setCompanies((prev) => {
          if (companyPage === 1) return incoming
          const existingIds = new Set(prev.map((company) => company.companyId))
          const merged = [...prev]
          for (const company of incoming) {
            if (!existingIds.has(company.companyId)) {
              merged.push(company)
              existingIds.add(company.companyId)
            }
          }
          return merged
        })
      })
      .catch((error) => {
        console.error('Failed to load contractor companies', error)
        setCompanies([])
        setCompanyTotalPages(0)
        setCompanyTotalItems(0)
      })
      .finally(() => {
        if (!cancelled) {
          setIsCompanyLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [companyPage, companySearchTerm, isOpen, tenantId])

  useEffect(() => {
    if (!isOpen) return

    const timer = window.setTimeout(() => {
      setCompanySearchTerm(companySearchInput.trim())
      setCompanyPage(1)
      setCompanies([])
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
    }
  }, [companySearchInput, isOpen])

  useEffect(() => {
    if (!isOpen) return

    const timer = window.setTimeout(() => {
      setSearchTerm(searchInput.trim())
      setWorkersPage(1)
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
    }
  }, [searchInput, isOpen])

  useEffect(() => {
    if (!isOpen) return

    let cancelled = false
    setIsWorkersLoading(true)

    getContractorWorkers({
      // tenantId,
      // Worker lookup in permit-request should not be restricted by tenant.
      search: searchTerm || undefined,
      companyId: selectedCompanyId === 'all' ? undefined : selectedCompanyId,
      pageNumber: workersPage,
      pageSize: DEFAULT_PAGE_SIZE,
    })
      .then((result) => {
        if (cancelled) return
        setWorkers(result.items ?? [])
        setWorkersTotalPages(result.totalPages ?? 0)
        setWorkersTotalItems(result.totalItems ?? 0)
      })
      .catch((error) => {
        console.error('Failed to load contractor workers', error)
        setWorkers([])
        setWorkersTotalPages(0)
        setWorkersTotalItems(0)
      })
      .finally(() => {
        if (!cancelled) {
          setIsWorkersLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [tenantId, searchTerm, selectedCompanyId, workersPage, isOpen])

  useEffect(() => {
    if (isOpen) return
    setSearchInput('')
    setSearchTerm('')
    setSelectedCompanyId('all')
    setCompanySearchInput('')
    setCompanySearchTerm('')
    setCompanyPage(1)
    setCompanyTotalPages(0)
    setCompanyTotalItems(0)
    setWorkersPage(1)
  }, [isOpen])

  const canLoadMoreCompanies = companyPage < companyTotalPages && !isCompanyLoading

  const handleLoadMoreCompanies = () => {
    if (canLoadMoreCompanies) {
      setCompanyPage((prev) => prev + 1)
    }
  }

  const handleToggleWorker = (worker: ContractorWorker) => {
    const exists = selectedWorkers.some((selected) => selected.workerId === worker.workerId)
    if (exists) {
      setSelectedWorkers(selectedWorkers.filter((selected) => selected.workerId !== worker.workerId))
    } else {
      const normalizedCompanyName = formatCompanyName(resolveWorkerCompanyName(worker))
      const normalizedJobRole = resolveWorkerJobRole(worker)
      setSelectedWorkers([
        ...selectedWorkers,
        {
          workerId: worker.workerId,
          companyId: worker.companyId,
          companyName: normalizedCompanyName,
          orgName: worker.orgName ?? null,
          fullName: worker.fullName,
          username: worker.username,
          imageType: worker.imageType,
          imagePath: worker.imagePath,
          jobRole: normalizedJobRole,
          roleInPermit: null,
          status: worker.status,
          cardNumber: worker.cardNumber,
        },
      ])
    }
  }

  const handleClearAll = () => {
    setSelectedWorkers([])
  }

  const selectedWorkerIds = useMemo(
    () => new Set(selectedWorkers.map((worker) => worker.workerId)),
    [selectedWorkers],
  )

  const mergedWorkersById = useMemo(() => {
    const grouped = new Map<string, ContractorWorker[]>()

    for (const worker of workers) {
      const existing = grouped.get(worker.workerId)
      if (existing) {
        existing.push(worker)
      } else {
        grouped.set(worker.workerId, [worker])
      }
    }

    const merged = new Map<string, ContractorWorker>()
    grouped.forEach((records, workerId) => {
      const selectedMatch = selectedWorkers.find((worker) => worker.workerId === workerId) ?? null
      merged.set(workerId, mergeWorkerDisplayRecord(records, selectedMatch))
    })

    return merged
  }, [selectedWorkers, workers])

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          <CardTitle>ผู้ปฏิบัติงาน</CardTitle>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              เลือกผู้ปฏิบัติงาน
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Select Workers (เลือกผู้ปฏิบัติงาน)
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5">
              <div className="grid gap-3 md:grid-cols-3">
                <div className="md:col-span-2">
                  <Label className="text-sm text-gray-600">Search Users</Label>
                  <div className="relative mt-1">
                    <Input
                      placeholder="Search by username, name, ID, position..."
                      value={searchInput}
                      onChange={(event) => setSearchInput(event.target.value)}
                      className="pl-10"
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">Filter by Company</Label>
                  <Select
                    value={selectedCompanyId}
                    onValueChange={(value) => {
                      setSelectedCompanyId(value as string | 'all')
                      setWorkersPage(1)
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="All Companies" />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="p-1">
                        <Input
                          placeholder="Search company..."
                          value={companySearchInput}
                          onChange={(event) => setCompanySearchInput(event.target.value)}
                          onKeyDown={(event) => event.stopPropagation()}
                          className="h-8"
                        />
                      </div>
                      <SelectItem value="all">All Companies</SelectItem>
                      {companies.map((company) => (
                        <SelectItem key={company.companyId} value={company.companyId}>
                          {company.companyName}
                        </SelectItem>
                      ))}
                      {!isCompanyLoading && companies.length === 0 ? (
                        <SelectItem value="no-results" disabled>
                          ไม่พบบริษัท
                        </SelectItem>
                      ) : null}
                      {isCompanyLoading ? (
                        <SelectItem value="loading" disabled>
                          กำลังโหลด...
                        </SelectItem>
                      ) : null}
                      {canLoadMoreCompanies ? (
                        <div className="px-2 pb-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={handleLoadMoreCompanies}
                          >
                            โหลดบริษัทเพิ่ม ({companies.length}/{companyTotalItems})
                          </Button>
                        </div>
                      ) : null}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-medium text-blue-900">
                    ผู้ปฏิบัติงานที่เลือกแล้ว ({selectedWorkers.length})
                  </p>
                  {hasSelection ? (
                    <Button variant="link" className="h-auto p-0 text-blue-600" onClick={handleClearAll}>
                      ล้างทั้งหมด
                    </Button>
                  ) : null}
                </div>
                {hasSelection ? (
                   <div className="flex flex-wrap gap-2">
                     {selectedWorkers.map((worker) => {
                       const identifierDisplay = resolveIdentifierDisplay(worker)
                       return (
                       <Badge
                          key={worker.workerId}
                          variant="secondary"
                          className="flex items-center gap-2 bg-blue-100 px-3 py-1 text-blue-800"
                        >
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-200 text-blue-700">
                            <User className="h-4 w-4" />
                          </span>
                          <span>
                            {worker.fullName}
                            {identifierDisplay ? ` (${identifierDisplay})` : ""}
                          </span>
                          <button
                            type="button"
                            className="text-blue-700 hover:text-blue-900"
                          onClick={() =>
                            setSelectedWorkers(selectedWorkers.filter((item) => item.workerId !== worker.workerId))
                          }
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                       )
                     })}
                  </div>
                ) : (
                  <p className="text-sm text-blue-700">ยังไม่มีการเลือกผู้ปฏิบัติงาน</p>
                )}
              </div>

              <div className="rounded-lg border">
                <div className="flex items-center justify-between border-b bg-gray-50 px-4 py-2 text-sm text-gray-600">
                  <span>{isWorkersLoading ? 'กำลังโหลด...' : `${workersTotalItems} workers found`}</span>
                  <span>
                    Page {workersPage} / {Math.max(workersTotalPages, 1)}
                  </span>
                </div>
                <div className="max-h-80 overflow-y-auto divide-y">
                  {isWorkersLoading ? (
                    <div className="flex items-center justify-center py-10 text-gray-500">
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      กำลังโหลดข้อมูลผู้ปฏิบัติงาน...
                    </div>
                  ) : workers.length === 0 ? (
                    <div className="py-10 text-center text-gray-500">ไม่พบผู้ปฏิบัติงาน</div>
                  ) : (
                   workers.map((worker) => {
                      const displayWorker = mergedWorkersById.get(worker.workerId) ?? worker
                      const isSelected = selectedWorkerIds.has(worker.workerId)
                      const companyDisplayName = formatCompanyName(resolveWorkerCompanyName(displayWorker))
                      const orgDisplayName = formatCompanyName(displayWorker.orgName)
                      const positionDisplayName = formatCompanyName(resolveWorkerJobRole(displayWorker))
                      const identifierDisplay = resolveIdentifierDisplay(displayWorker)
                      const imageSrc = buildWorkerImageSrc(displayWorker)
 
                      return (
                        <div
                          key={worker.workerId}
                          className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between"
                        >
                          <div className="flex items-start gap-3">
                            <WorkerAvatar imageSrc={imageSrc} alt={worker.fullName} sizePx={60} iconSizePx={32} />
                            <div className="min-w-0">
                              <p className="truncate font-medium text-gray-900">{worker.fullName}</p>
                              <p className="text-sm text-gray-600">{positionDisplayName}</p>
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                                <Building2 className="h-4 w-4" />
                                <span>{companyDisplayName}</span>
                                <span className="text-gray-400">|</span>
                                <span>{orgDisplayName}</span>
                                {identifierDisplay ? (
                                  <>
                                    <span className="text-gray-400">|</span>
                                    <span>{identifierDisplay}</span>
                                  </>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button
                              variant={isSelected ? 'outline' : 'default'}
                              className={
                                isSelected
                                  ? 'border-red-200 text-red-600 hover:bg-red-50'
                                  : 'flex items-center gap-2'
                              }
                              onClick={() => handleToggleWorker(displayWorker)}
                            >
                              {isSelected ? (
                                <>
                                  <X className="h-4 w-4" />
                                  ลบ
                                </>
                              ) : (
                                <>
                                  <UserPlus className="h-4 w-4" />
                                  เพิ่ม
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
                <div className="flex items-center justify-between border-t bg-gray-50 px-4 py-3">
                  <span className="text-sm text-gray-500">แสดง {workers.length} รายการ / หน้า</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={workersPage <= 1 || isWorkersLoading}
                      onClick={() => setWorkersPage((prev) => Math.max(1, prev - 1))}
                    >
                      ก่อนหน้า
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={
                        workersPage >= workersTotalPages || isWorkersLoading || workersTotalPages === 0
                      }
                      onClick={() =>
                        setWorkersPage((prev) =>
                          workersTotalPages === 0 ? prev : Math.min(workersTotalPages, prev + 1),
                        )
                      }
                    >
                      ถัดไป
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasSelection ? (
          <div className="grid gap-3">
            {selectedWorkers.map((worker) => {
              const identifierDisplay = resolveIdentifierDisplay(worker)
              return (
                <div
                  key={worker.workerId}
                  className="flex flex-col gap-3 rounded-xl border border-sky-100 bg-white px-4 py-3 shadow-sm md:flex-row md:items-center md:justify-between"
                >
                  <div className="flex items-start gap-3">
                    <WorkerAvatar
                      imageSrc={buildWorkerSelectionImageSrc(worker)}
                      alt={worker.fullName}
                      sizePx={60}
                      iconSizePx={28}
                    />
                    <div className="min-w-0">
                      <p className="truncate font-medium text-sky-900">{worker.fullName}</p>
                      <p className="text-sm text-sky-700">{worker.roleInPermit ?? worker.jobRole}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-sky-600">
                        <Building2 className="h-4 w-4 shrink-0 text-sky-500" />
                        <span>{formatCompanyName(worker.companyName)}</span>
                        {worker.orgName ? (
                          <>
                            <span className="text-sky-300">|</span>
                            <span>{formatCompanyName(worker.orgName)}</span>
                          </>
                        ) : null}
                        {identifierDisplay ? (
                          <>
                            <span className="text-sky-300">|</span>
                            <span>{identifierDisplay}</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    className="shrink-0 self-end rounded-lg border border-red-100 bg-red-50 px-3 text-red-600 hover:border-red-200 hover:bg-red-100 hover:text-red-700 md:self-center"
                    onClick={() =>
                      setSelectedWorkers(selectedWorkers.filter((item) => item.workerId !== worker.workerId))
                    }
                  >
                    <X className="mr-2 h-4 w-4" />
                    ลบ
                  </Button>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-sky-200 bg-sky-50/40 p-6 text-center text-sky-700">
            ยังไม่มีผู้ปฏิบัติงานถูกเลือก
          </div>
        )}
      </CardContent>
    </Card>
  )
}
